// Phase G G5 — viewer page /live/$slug. Minimal chrome (full-page player, not the
// app sidebar). Public route: anon may reach it and gets sign-in; non-members get
// the subscribe CTA; members connect. Never captures camera/mic.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import { GuestQueueBar } from '@/features/live/GuestQueueBar';
import { GuestStage } from '@/features/live/GuestStage';
import {
  LiveServiceError,
  liveRoomService,
  makeIdentity,
  type LiveErrorCode,
} from '@/features/live/liveRoomService';
import { hostDisplayName, roomQueries } from '@/features/live/roomQueries';
import { useGuestQueue } from '@/features/live/useGuestQueue';
import { ViewerStage } from '@/features/live/ViewerStage';
import { decideViewerAccess } from '@/features/live/viewerAccess';
import { useAuth } from '@/lib/auth/useAuth';

export const Route = createFileRoute('/live/$slug')({
  component: LiveViewerRoute,
});

function Shell({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen flex-col bg-video-bg text-video-text">{children}</div>;
}

function Centered({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: { href: string; label: string };
}) {
  return (
    <Shell>
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
        <h1 className="font-serif text-2xl">{title}</h1>
        {body && <p className="max-w-md font-sans text-sm text-video-text-muted">{body}</p>}
        {action && (
          <a
            href={action.href}
            className="mt-2 rounded-md bg-video-text px-5 py-2 font-sans text-sm font-medium text-video-bg transition hover:opacity-90"
          >
            {action.label}
          </a>
        )}
        <a href="/" className="font-sans text-xs text-video-text-faint hover:underline">
          Back to browse
        </a>
      </div>
    </Shell>
  );
}

function Status({ label }: { label: string }) {
  return (
    <Shell>
      <div className="flex flex-1 items-center justify-center">
        <p className="font-sans text-sm text-video-text-faint">{label}</p>
      </div>
    </Shell>
  );
}

function LiveViewerRoute() {
  const { slug } = Route.useParams();
  const { session } = useAuth();
  const hasSession = Boolean(session);

  const roomQuery = useQuery(roomQueries.bySlug(slug));
  const room = roomQuery.data;
  const access = decideViewerAccess(room, hasSession);

  const tokenQuery = useQuery({
    queryKey: ['live-token', room?.livekitRoomName, session?.user.id],
    enabled: access.state === 'authorize' && Boolean(room && session),
    retry: false,
    staleTime: Infinity,
    queryFn: () =>
      // role is server-derived; 'viewer' is the requested role and is ignored.
      liveRoomService.mintAccessToken(
        room!.livekitRoomName,
        makeIdentity(session!.user.id),
        'viewer',
      ),
  });

  // Guest queue (Inc 4a): a single subscription that drives BOTH the bar and
  // promotion detection. Only when authorized on a queue-enabled room.
  const queueRoomId = access.state === 'authorize' && room?.queueEnabled ? room.id : '';
  const queue = useGuestQueue(queueRoomId);
  const onGrid = queue.data?.yourStatus === 'on_grid';
  // A guest who leaves stays on-grid (they keep their paid slot), so guard against
  // instantly re-promoting them; reset once they actually drop off (demoted).
  const [hasLeft, setHasLeft] = useState(false);
  useEffect(() => {
    if (!onGrid) setHasLeft(false);
  }, [onGrid]);
  const promoted = onGrid && !hasLeft;

  if (roomQuery.isLoading) return <Status label="Loading…" />;

  if (access.state === 'not_found') {
    return (
      <Centered
        title="This stream isn't available"
        body="The link may be wrong, or the room no longer exists."
      />
    );
  }
  if (access.state === 'not_live') {
    return (
      <Centered
        title={
          access.status === 'ended' ? 'This stream has ended' : 'This stream hasn’t started yet'
        }
        body={room ? `${room.title} · ${hostDisplayName(room.host)}` : undefined}
      />
    );
  }
  if (access.state === 'needs_signin') {
    return (
      <Centered
        title="Sign in to watch"
        body="Live shows are for members. Sign in, then subscribe to watch."
        action={{ href: '/auth/sign-in', label: 'Sign in' }}
      />
    );
  }

  // authorize → mint a viewer token (role server-derived).
  if (tokenQuery.isLoading) return <Status label="Connecting…" />;
  if (tokenQuery.error) {
    const code: LiveErrorCode =
      tokenQuery.error instanceof LiveServiceError ? tokenQuery.error.code : 'unknown';
    if (code === 'must_subscribe') {
      return (
        <Centered
          title="Members only"
          body="Subscribe to watch live shows."
          action={{ href: '/subscribe', label: 'Subscribe' }}
        />
      );
    }
    if (code === 'unauthenticated') {
      return (
        <Centered title="Sign in to watch" action={{ href: '/auth/sign-in', label: 'Sign in' }} />
      );
    }
    if (code === 'room_not_found') return <Centered title="This stream isn't available" />;
    return <Centered title="Something went wrong" body="Please try again in a moment." />;
  }
  if (!tokenQuery.data || !room) return <Status label="Connecting…" />;

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-video-bg text-video-text">
      <header className="flex items-center gap-3 border-b border-video-line px-4 py-3">
        <span className="rounded-sm bg-oxblood-live px-2 py-0.5 font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text">
          Live
        </span>
        <span className="min-w-0">
          <span className="block truncate font-serif text-base">{room.title}</span>
          <span className="font-sans text-xs text-video-text-muted">
            {hostDisplayName(room.host)}
          </span>
        </span>
        <a href="/" className="ml-auto font-sans text-xs text-video-text-faint hover:underline">
          Browse
        </a>
      </header>
      <main className="min-h-0 flex-1" aria-live="polite">
        {promoted ? (
          // Paid grid slot → publish on camera. On leave/demote, drop back here.
          <GuestStage
            room={room}
            onLeave={() => setHasLeft(true)}
            graceUntil={queue.data?.yourGraceUntil ?? null}
          />
        ) : (
          <ViewerStage
            token={tokenQuery.data.token}
            serverUrl={tokenQuery.data.url}
            roomStatus={room.status}
            hostId={room.hostId}
            title={room.title}
            tickerLines={room.tickerLines}
            logoUrl={room.logoUrl}
            guestBar={
              room.queueEnabled ? <GuestQueueBar room={room} queue={queue.data} /> : undefined
            }
          />
        )}
      </main>
    </div>
  );
}
